import {
  Box,
  Chip,
  Stack,
  Typography,
  alpha,
  useTheme,
} from "@mui/material";
import type { ReactElement, ReactNode } from "react";

interface DocumentCardProps {
  title: string;
  date?: string;
  /** Rótulo da data principal (ex.: "Emitido em"). */
  dateLabel?: string;
  /** Data da última remissão — exibida só quando diferente da primeira. */
  lastDate?: string;
  lastDateLabel?: string;
  /** Quando true e sem lastDate distinta, indica emissão única no card. */
  singleIssue?: boolean;
  /** Ação opcional ao lado das datas (ex.: histórico de remissões). */
  dateAction?: ReactNode;
  practitioner?: string;
  specialty?: string;
  status?: {
    text: string;
    color: string;
    icon: ReactElement;
  };
  actions?: ReactNode;
  children?: ReactNode;
}

/** Card de documento clínico — layout mobile moderno. */
export function DocumentCard({
  title,
  date,
  dateLabel,
  lastDate,
  lastDateLabel = "Última remissão",
  singleIssue = false,
  dateAction,
  practitioner,
  specialty,
  status,
  actions,
  children,
}: DocumentCardProps) {
  const theme = useTheme();
  const specialtyLabel =
    specialty && specialty !== "-" && specialty !== "undefined"
      ? specialty
      : undefined;

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        borderRadius: 2.5,
        border: "1px solid",
        borderColor: "divider",
        borderLeftWidth: 4,
        borderLeftColor: status?.color || theme.palette.primary.main,
        bgcolor: "background.paper",
        overflow: "hidden",
        boxShadow: "0 2px 12px rgba(15, 23, 42, 0.06)",
      }}
    >
      <Stack spacing={1} sx={{ px: 1.35, py: 1.25, flex: 1, minWidth: 0 }}>
        {/* Linha 1: título + badge de categoria */}
        <Stack
          direction="row"
          alignItems="flex-start"
          justifyContent="space-between"
          gap={1}
        >
          <Typography
            variant="subtitle2"
            fontWeight={800}
            sx={{
              flex: 1,
              minWidth: 0,
              fontSize: "0.9375rem",
              lineHeight: 1.35,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {title}
          </Typography>

          {status ? (
            <Chip
              icon={status.icon}
              label={status.text}
              size="small"
              sx={{
                height: 24,
                fontWeight: 700,
                fontSize: "0.65rem",
                color: status.color,
                bgcolor: alpha(status.color, 0.12),
                border: "1px solid",
                borderColor: alpha(status.color, 0.22),
                flexShrink: 0,
                "& .MuiChip-icon": { color: status.color },
              }}
            />
          ) : null}
        </Stack>

        {/* Linha 2: metadados */}
        {(date || lastDate || practitioner || specialtyLabel) && (
          <Stack spacing={0.35}>
            {date ? (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  fontWeight={600}
                >
                  {dateLabel ? `${dateLabel}: ${date}` : date}
                </Typography>
                {dateAction}
              </Stack>
            ) : null}
            {lastDate && lastDate !== date ? (
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
              >
                {lastDateLabel}: {lastDate}
              </Typography>
            ) : singleIssue ? (
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
              >
                Emissão única
              </Typography>
            ) : null}
            {practitioner ? (
              <Typography
                variant="caption"
                color="text.secondary"
                fontWeight={600}
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: 1,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                }}
              >
                {practitioner}
                {specialtyLabel ? ` · ${specialtyLabel}` : ""}
              </Typography>
            ) : null}
          </Stack>
        )}

        {/* Conteúdo extra (ex.: resultado disponível) */}
        {children ? (
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              bgcolor: alpha(theme.palette.primary.main, 0.04),
              border: "1px solid",
              borderColor: alpha(theme.palette.primary.main, 0.08),
            }}
          >
            {children}
          </Box>
        ) : null}
      </Stack>

      {/* Rodapé: ações em stack vertical */}
      {actions ? (
        <Box
          sx={{
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: (t) => t.palette.action.hover,
            "& .MuiTableCell-root": {
              display: "block",
              borderBottom: "none !important",
              p: 0,
              width: "100%",
            },
          }}
        >
          {actions}
        </Box>
      ) : null}
    </Box>
  );
}
